import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  VStack,
  HStack,
  Button,
  Text,
  Heading,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Select,
  Switch,
  useColorModeValue,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  useToast,
  Alert,
  AlertIcon,
  Card,
  CardBody,
  CardHeader,
  Divider,
  IconButton,
  Badge,
  SimpleGrid,
  Flex,
  FormErrorMessage,
  Tab,
  Tabs,
  TabList,
  TabPanels,
  TabPanel,
  Code,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
} from '@chakra-ui/react';
import { ChevronRightIcon, AddIcon, DeleteIcon, DragHandleIcon } from '@chakra-ui/icons';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import { 
  FiSave, 
  FiArrowLeft, 
  FiPlus, 
  FiTrash2, 
  FiMove,
  FiEye,
  FiCode,
  FiSettings,
  FiGrid,
  FiLayers
} from 'react-icons/fi';
import apiClient from '../../../utils/apiClient';

const LayoutForm = () => {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const toast = useToast();

  const user = JSON.parse(sessionStorage.getItem('user') || '{}');
  const tenantId = user?.tenant_ids?.[0];

  const [loading, setLoading] = useState(false);
  const [components, setComponents] = useState([]);
  const [newSectionName, setNewSectionName] = useState('');
  const { isOpen: isAddSectionOpen, onOpen: onAddSectionOpen, onClose: onAddSectionClose } = useDisclosure();
  const [errors, setErrors] = useState({});
  const [activeTab, setActiveTab] = useState(0);
  
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category: 'custom',
    layout_data: {
      sections: [
        {
          id: 'header',
          name: 'Header',
          type: 'header',
          components: [],
          settings: {
            background: '#ffffff',
            padding: '1rem',
            maxWidth: '1200px'
          }
        },
        {
          id: 'main',
          name: 'Main Content',
          type: 'main',
          components: [],
          settings: {
            background: '#ffffff',
            padding: '2rem',
            maxWidth: '1200px'
          }
        },
        {
          id: 'footer',
          name: 'Footer',
          type: 'footer',
          components: [],
          settings: {
            background: '#f7fafc',
            padding: '2rem',
            maxWidth: '1200px'
          }
        }
      ],
      global_settings: {
        container_width: '1200px',
        font_family: 'system-ui',
        color_scheme: 'light'
      }
    },
    preview_image: '',
    is_public: false,
    is_active: true
  });

  const cardBg = useColorModeValue('white', 'gray.700');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const sectionBg = useColorModeValue('gray.50', 'gray.600');

  useEffect(() => {
    if (tenantId) {
      fetchComponents();
      if (isEdit) {
        fetchLayout();
      }
    }
  }, [tenantId, id]);

  const fetchComponents = async () => {
    try {
      const response = await apiClient(`/page-components/${tenantId}`);
      console.log('Raw API response:', response);
      
      let componentsData = [];
      
      // Handle the nested array structure from your API
      if (Array.isArray(response)) {
        // If response is directly an array, check if it contains nested arrays
        if (response.length > 0 && Array.isArray(response[0])) {
          // Flatten the nested array structure
          componentsData = response.flat();
        } else {
          componentsData = response;
        }
      } else if (response.success && Array.isArray(response.data)) {
        // If response has a success flag and data property
        componentsData = response.data.flat();
      } else if (response.data) {
        // If response has a data property
        if (Array.isArray(response.data)) {
          componentsData = Array.isArray(response.data[0]) ? response.data.flat() : response.data;
        }
      } else {
        // Fallback: try to extract arrays from object properties
        const possibleArrays = Object.values(response).filter(value => Array.isArray(value));
        if (possibleArrays.length > 0) {
          componentsData = possibleArrays.flat();
        }
      }
      
      // Filter out any invalid entries and ensure we have component objects
      componentsData = componentsData.filter(component => 
        component && 
        typeof component === 'object' && 
        component.id && 
        component.name && 
        component.identifier
      );
      
      console.log('Processed components:', componentsData);
      setComponents(componentsData);
      
      if (componentsData.length === 0) {
        toast({
          title: 'No components found',
          description: 'No available components were found for this tenant',
          status: 'warning',
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error('Error fetching components:', error);
      toast({
        title: 'Error loading components',
        description: 'Failed to load available components',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      setComponents([]);
    }
  };

  const fetchLayout = async () => {
    try {
      setLoading(true);
      const response = await apiClient(`/component-layouts/${tenantId}/${id}`);
      
      if (response.error) {
        throw new Error(response.error);
      }

      const layoutData = response.data || response;
      
      // Check if layout_data is already an object or if it's a JSON string
      let parsedLayoutData = layoutData.layout_data;
      
      if (typeof layoutData.layout_data === 'string') {
        try {
          parsedLayoutData = JSON.parse(layoutData.layout_data);
        } catch (parseError) {
          console.error('Error parsing layout_data JSON:', parseError);
          // Fall back to default layout structure if parsing fails
          parsedLayoutData = formData.layout_data;
        }
      } else if (!layoutData.layout_data) {
        // If layout_data is null/undefined, use default
        parsedLayoutData = formData.layout_data;
      }
      
      setFormData({
        name: layoutData.name || '',
        description: layoutData.description || '',
        category: layoutData.category || 'custom',
        layout_data: parsedLayoutData,
        preview_image: layoutData.preview_image || '',
        is_public: Boolean(layoutData.is_public),
        is_active: layoutData.is_active !== undefined ? Boolean(layoutData.is_active) : true
      });
    } catch (error) {
      console.error('Error fetching layout:', error);
      toast({
        title: 'Error loading layout',
        description: error.message || 'Failed to load layout',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: null
      }));
    }
  };

  const handleLayoutDataChange = (newLayoutData) => {
    console.log('Layout data changing from:', formData.layout_data);
    console.log('Layout data changing to:', newLayoutData);
    
    setFormData(prev => {
      const updated = {
        ...prev,
        layout_data: {
          ...newLayoutData,
          sections: [...newLayoutData.sections], // Ensure array is copied
          global_settings: { ...newLayoutData.global_settings }
        }
      };
      console.log('Updated formData:', updated);
      return updated;
    });
  };

  const addSection = (sectionName = null) => {
    const name = sectionName || newSectionName || 'New Section';
    console.log('Adding new section with name:', name);
    
    const newSection = {
      id: `section_${Date.now()}`,
      name: name,
      type: 'section',
      components: [],
      settings: {
        background: '#ffffff',
        padding: '2rem',
        maxWidth: '1200px'
      }
    };

    // Use functional update to ensure we have the latest state
    setFormData(prev => {
      const newLayoutData = {
        ...prev.layout_data,
        sections: [...prev.layout_data.sections, newSection]
      };
      
      console.log('Previous sections:', prev.layout_data.sections);
      console.log('New sections:', newLayoutData.sections);
      
      return {
        ...prev,
        layout_data: newLayoutData
      };
    });
    
    // Reset form and close modal
    setNewSectionName('');
    onAddSectionClose();
    
    toast({
      title: 'Section added',
      description: `"${name}" has been added to your layout`,
      status: 'success',
      duration: 2000,
      isClosable: true,
    });
  };

  const handleAddSectionSubmit = () => {
    if (!newSectionName.trim()) {
      toast({
        title: 'Section name required',
        description: 'Please enter a name for the section',
        status: 'error',
        duration: 2000,
        isClosable: true,
      });
      return;
    }
    addSection(newSectionName);
  };

  const updateSection = (sectionIndex, updates) => {
    console.log('Updating section:', sectionIndex, updates);
    
    setFormData(prev => {
      const newSections = prev.layout_data.sections.map((section, index) =>
        index === sectionIndex ? { ...section, ...updates } : section
      );

      const newLayoutData = {
        ...prev.layout_data,
        sections: newSections
      };

      console.log('Section update - Previous:', prev.layout_data.sections[sectionIndex]);
      console.log('Section update - New:', newSections[sectionIndex]);
      
      return {
        ...prev,
        layout_data: newLayoutData
      };
    });
  };

  const removeSection = (sectionIndex) => {
    console.log('Removing section:', sectionIndex);
    const newLayoutData = {
      ...formData.layout_data,
      sections: formData.layout_data.sections.filter((_, index) => index !== sectionIndex)
    };

    handleLayoutDataChange(newLayoutData);
    
    toast({
      title: 'Section removed',
      description: 'Section has been removed from your layout',
      status: 'info',
      duration: 2000,
      isClosable: true,
    });
  };

  const addComponentToSection = (sectionIndex, componentId) => {
    console.log('Adding component to section:', sectionIndex, componentId);
    
    const numericComponentId = typeof componentId === 'string' ? parseInt(componentId) : componentId;
    const component = components.find(c => c.id === numericComponentId);
    
    if (!component) {
      console.error('Component not found:', componentId, 'Available components:', components);
      toast({
        title: 'Error adding component',
        description: 'Component not found',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    const newComponent = {
      id: `${component.identifier}_${Date.now()}`,
      component_id: numericComponentId,
      component_name: component.name,
      component_identifier: component.identifier,
      position: 0, // Will be set correctly below
      data: component.default_data ? JSON.parse(component.default_data) : {}
    };

    // Use functional update to ensure we have the latest state
    setFormData(prev => {
      const newSections = prev.layout_data.sections.map((section, index) => {
        if (index === sectionIndex) {
          const updatedComponents = [...section.components, {
            ...newComponent,
            position: section.components.length
          }];
          return { ...section, components: updatedComponents };
        }
        return section;
      });

      const newLayoutData = {
        ...prev.layout_data,
        sections: newSections
      };

      console.log('Previous layout data:', prev.layout_data);
      console.log('New layout data with component:', newLayoutData);
      
      return {
        ...prev,
        layout_data: newLayoutData
      };
    });
    
    toast({
      title: 'Component added',
      description: `${component.name} has been added to the section`,
      status: 'success',
      duration: 2000,
      isClosable: true,
    });
  };

  const removeComponentFromSection = (sectionIndex, componentIndex) => {
    console.log('Removing component from section:', sectionIndex, componentIndex);
    const newLayoutData = {
      ...formData.layout_data,
      sections: formData.layout_data.sections.map((section, index) =>
        index === sectionIndex 
          ? { 
              ...section, 
              components: section.components.filter((_, cIndex) => cIndex !== componentIndex)
            }
          : section
      )
    };

    handleLayoutDataChange(newLayoutData);
    
    toast({
      title: 'Component removed',
      description: 'Component has been removed from the section',
      status: 'info',
      duration: 2000,
      isClosable: true,
    });
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Layout name is required';
    }

    if (!formData.category) {
      newErrors.category = 'Category is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      toast({
        title: 'Validation Error',
        description: 'Please fix the errors below',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    try {
      setLoading(true);

      // Log the current form data to verify it has the latest changes
      console.log('Current formData before submit:', formData);
      console.log('Layout data structure:', formData.layout_data);
      console.log('Sections count:', formData.layout_data.sections.length);
      console.log('Sections details:', formData.layout_data.sections);

      // Create a fresh copy of the form data to ensure we have the latest state
      const currentFormData = {
        name: formData.name,
        description: formData.description,
        category: formData.category,
        layout_data: {
          ...formData.layout_data,
          sections: [...formData.layout_data.sections],
          global_settings: { ...formData.layout_data.global_settings }
        },
        preview_image: formData.preview_image,
        is_public: formData.is_public,
        is_active: formData.is_active
      };

      const submitData = {
        ...currentFormData,
        layout_data: JSON.stringify(currentFormData.layout_data),
        tenant_id: tenantId
      };

      console.log('Data being submitted:', submitData);
      console.log('Stringified layout_data:', submitData.layout_data);

      const url = isEdit 
        ? `/component-layouts/${tenantId}/${id}`
        : `/component-layouts/${tenantId}`;
      
      const method = isEdit ? 'PUT' : 'POST';

      const response = await apiClient(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(submitData)
      });

      console.log('API Response:', response);

      if (response.error) {
        throw new Error(response.error);
      }

      toast({
        title: `Layout ${isEdit ? 'updated' : 'created'} successfully`,
        description: `"${formData.name}" has been ${isEdit ? 'updated' : 'created'}`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      navigate('/layout-library');
    } catch (error) {
      console.error('Error saving layout:', error);
      toast({
        title: `Error ${isEdit ? 'updating' : 'creating'} layout`,
        description: error.message || `Failed to ${isEdit ? 'update' : 'create'} layout`,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const categories = [
    { value: 'header', label: 'Header' },
    { value: 'footer', label: 'Footer' },
    { value: 'section', label: 'Section' },
    { value: 'page', label: 'Full Page' },
    { value: 'custom', label: 'Custom' }
  ];

  if (!tenantId) {
    return (
      <Container maxW="container.xl" py={6}>
        <Alert status="error">
          <AlertIcon />
          No tenant ID found. Please log in again.
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxW="container.2xl" py={6}>
      <Breadcrumb mb={6} separator={<ChevronRightIcon color="gray.500" />}>
        <BreadcrumbItem>
          <BreadcrumbLink as={RouterLink} to="/">Dashboard</BreadcrumbLink>
        </BreadcrumbItem>
        <BreadcrumbItem>
          <BreadcrumbLink as={RouterLink} to="/pages">Pages</BreadcrumbLink>
        </BreadcrumbItem>
        <BreadcrumbItem>
          <BreadcrumbLink as={RouterLink} to="/layout-library">
            Layout Library
          </BreadcrumbLink>
        </BreadcrumbItem>
        <BreadcrumbItem isCurrentPage>
          <BreadcrumbLink>
            {isEdit ? 'Edit Layout' : 'Create Layout'}
          </BreadcrumbLink>
        </BreadcrumbItem>
      </Breadcrumb>

      {/* Add Section Modal */}
      <Modal isOpen={isAddSectionOpen} onClose={onAddSectionClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Add New Section</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <FormControl>
              <FormLabel>Section Name</FormLabel>
              <Input
                value={newSectionName}
                onChange={(e) => setNewSectionName(e.target.value)}
                placeholder="e.g., Hero Section, Content Area, Footer"
                autoFocus
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    handleAddSectionSubmit();
                  }
                }}
              />
            </FormControl>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onAddSectionClose}>
              Cancel
            </Button>
            <Button 
              colorScheme="blue" 
              onClick={handleAddSectionSubmit}
              isDisabled={!newSectionName.trim()}
            >
              Add Section
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <form onSubmit={handleSubmit}>
        <VStack spacing={6} align="stretch">
          <HStack justify="space-between">
            <Heading size="lg">
              {isEdit ? 'Edit Layout' : 'Create Layout'}
            </Heading>
            <HStack spacing={3}>
              <Button
                leftIcon={<FiArrowLeft />}
                variant="outline"
                onClick={() => navigate('/layout-library')}
              >
                Back
              </Button>
              <Button
                leftIcon={<FiSave />}
                colorScheme="blue"
                type="submit"
                isLoading={loading}
                loadingText={isEdit ? 'Updating...' : 'Creating...'}
              >
                {isEdit ? 'Update Layout' : 'Create Layout'}
              </Button>
            </HStack>
          </HStack>

          <Tabs index={activeTab} onChange={setActiveTab}>
            <TabList>
              <Tab>
                <HStack>
                  <FiSettings />
                  <Text>Basic Settings</Text>
                </HStack>
              </Tab>
              <Tab>
                <HStack>
                  <FiLayers />
                  <Text>Layout Builder</Text>
                </HStack>
              </Tab>
              <Tab>
                <HStack>
                  <FiEye />
                  <Text>Preview</Text>
                </HStack>
              </Tab>
              <Tab>
                <HStack>
                  <FiCode />
                  <Text>JSON View</Text>
                </HStack>
              </Tab>
            </TabList>

            <TabPanels>
              {/* Basic Settings Tab */}
              <TabPanel px={0}>
                <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6}>
                  <Card bg={cardBg} borderColor={borderColor} borderWidth="1px">
                    <CardHeader>
                      <Heading size="md">Basic Information</Heading>
                    </CardHeader>
                    <CardBody>
                      <VStack spacing={4} align="stretch">
                        <FormControl isRequired isInvalid={errors.name}>
                          <FormLabel>Layout Name</FormLabel>
                          <Input
                            value={formData.name}
                            onChange={(e) => handleInputChange('name', e.target.value)}
                            placeholder="e.g., Blog Homepage Layout"
                          />
                          <FormErrorMessage>{errors.name}</FormErrorMessage>
                        </FormControl>

                        <FormControl isRequired isInvalid={errors.category}>
                          <FormLabel>Category</FormLabel>
                          <Select
                            value={formData.category}
                            onChange={(e) => handleInputChange('category', e.target.value)}
                            placeholder="Select category"
                          >
                            {categories.map(category => (
                              <option key={category.value} value={category.value}>
                                {category.label}
                              </option>
                            ))}
                          </Select>
                          <FormErrorMessage>{errors.category}</FormErrorMessage>
                        </FormControl>

                        <FormControl>
                          <FormLabel>Description</FormLabel>
                          <Textarea
                            value={formData.description}
                            onChange={(e) => handleInputChange('description', e.target.value)}
                            placeholder="Describe this layout..."
                            rows={3}
                          />
                        </FormControl>

                        <FormControl>
                          <FormLabel>Preview Image URL</FormLabel>
                          <Input
                            value={formData.preview_image}
                            onChange={(e) => handleInputChange('preview_image', e.target.value)}
                            placeholder="https://example.com/preview.jpg"
                          />
                        </FormControl>
                      </VStack>
                    </CardBody>
                  </Card>

                  <Card bg={cardBg} borderColor={borderColor} borderWidth="1px">
                    <CardHeader>
                      <Heading size="md">Global Settings</Heading>
                    </CardHeader>
                    <CardBody>
                      <VStack spacing={4} align="stretch">
                        <FormControl>
                          <FormLabel>Container Width</FormLabel>
                          <Input
                            value={formData.layout_data.global_settings.container_width}
                            onChange={(e) => {
                              const newLayoutData = {
                                ...formData.layout_data,
                                global_settings: {
                                  ...formData.layout_data.global_settings,
                                  container_width: e.target.value
                                }
                              };
                              handleLayoutDataChange(newLayoutData);
                            }}
                            placeholder="1200px"
                          />
                        </FormControl>

                        <FormControl>
                          <FormLabel>Font Family</FormLabel>
                          <Select
                            value={formData.layout_data.global_settings.font_family}
                            onChange={(e) => {
                              const newLayoutData = {
                                ...formData.layout_data,
                                global_settings: {
                                  ...formData.layout_data.global_settings,
                                  font_family: e.target.value
                                }
                              };
                              handleLayoutDataChange(newLayoutData);
                            }}
                          >
                            <option value="system-ui">System UI</option>
                            <option value="Arial, sans-serif">Arial</option>
                            <option value="Georgia, serif">Georgia</option>
                            <option value="'Times New Roman', serif">Times New Roman</option>
                            <option value="'Courier New', monospace">Courier New</option>
                          </Select>
                        </FormControl>

                        <FormControl>
                          <FormLabel>Color Scheme</FormLabel>
                          <Select
                            value={formData.layout_data.global_settings.color_scheme}
                            onChange={(e) => {
                              const newLayoutData = {
                                ...formData.layout_data,
                                global_settings: {
                                  ...formData.layout_data.global_settings,
                                  color_scheme: e.target.value
                                }
                              };
                              handleLayoutDataChange(newLayoutData);
                            }}
                          >
                            <option value="light">Light</option>
                            <option value="dark">Dark</option>
                            <option value="auto">Auto</option>
                          </Select>
                        </FormControl>

                        <Divider />

                        <FormControl display="flex" alignItems="center">
                          <FormLabel mb={0}>Public Layout</FormLabel>
                          <Switch
                            isChecked={formData.is_public}
                            onChange={(e) => handleInputChange('is_public', e.target.checked)}
                          />
                        </FormControl>

                        <FormControl display="flex" alignItems="center">
                          <FormLabel mb={0}>Active</FormLabel>
                          <Switch
                            isChecked={formData.is_active}
                            onChange={(e) => handleInputChange('is_active', e.target.checked)}
                          />
                        </FormControl>
                      </VStack>
                    </CardBody>
                  </Card>
                </SimpleGrid>
              </TabPanel>

              {/* Layout Builder Tab */}
              <TabPanel px={0}>
                <VStack spacing={6} align="stretch">
                  <HStack justify="space-between">
                    <Heading size="md">Layout Sections</Heading>
                    <Button
                      leftIcon={<FiPlus />}
                      size="sm"
                      onClick={onAddSectionOpen}
                    >
                      Add Section
                    </Button>
                  </HStack>

                  {formData.layout_data.sections.map((section, sectionIndex) => (
                    <LayoutSection
                      key={section.id}
                      section={section}
                      sectionIndex={sectionIndex}
                      components={components}
                      onUpdateSection={updateSection}
                      onRemoveSection={removeSection}
                      onAddComponent={addComponentToSection}
                      onRemoveComponent={removeComponentFromSection}
                      cardBg={cardBg}
                      borderColor={borderColor}
                      sectionBg={sectionBg}
                    />
                  ))}

                  {formData.layout_data.sections.length === 0 && (
                    <Box textAlign="center" py={12}>
                      <FiGrid size={48} color="gray.400" />
                      <Heading size="md" mt={4} mb={2} color="gray.500">
                        No sections defined
                      </Heading>
                      <Text color="gray.500" mb={6}>
                        Add sections to start building your layout
                      </Text>
                      <Button
                        leftIcon={<FiPlus />}
                        onClick={onAddSectionOpen}
                      >
                        Add Your First Section
                      </Button>
                    </Box>
                  )}
                </VStack>
              </TabPanel>

              {/* Preview Tab */}
              <TabPanel px={0}>
                <Card bg={cardBg} borderColor={borderColor} borderWidth="1px">
                  <CardHeader>
                    <Heading size="md">Layout Preview</Heading>
                  </CardHeader>
                  <CardBody>
                    <Box 
                      border="2px dashed" 
                      borderColor="gray.300" 
                      borderRadius="md" 
                      p={6}
                      bg="gray.50"
                    >
                      <VStack spacing={4} align="stretch">
                        {formData.layout_data.sections.map((section, index) => (
                          <Box
                            key={section.id}
                            bg={section.settings.background || 'white'}
                            p={4}
                            borderRadius="md"
                            border="1px solid"
                            borderColor="gray.200"
                          >
                            <HStack justify="space-between" mb={2}>
                              <Badge colorScheme="blue">{section.name}</Badge>
                              <Text fontSize="xs" color="gray.500">
                                {section.components.length} components
                              </Text>
                            </HStack>
                            
                            {section.components.length > 0 ? (
                              <VStack spacing={2} align="stretch">
                                {section.components.map((component, cIndex) => (
                                  <Box
                                    key={component.id}
                                    bg="blue.50"
                                    p={2}
                                    borderRadius="sm"
                                    fontSize="sm"
                                  >
                                    {component.component_name}
                                  </Box>
                                ))}
                              </VStack>
                            ) : (
                              <Text fontSize="sm" color="gray.500" fontStyle="italic">
                                No components added
                              </Text>
                            )}
                          </Box>
                        ))}
                      </VStack>
                    </Box>
                  </CardBody>
                </Card>
              </TabPanel>

              {/* JSON View Tab */}
              <TabPanel px={0}>
                <Card bg={cardBg} borderColor={borderColor} borderWidth="1px">
                  <CardHeader>
                    <Heading size="md">Layout JSON Structure</Heading>
                  </CardHeader>
                  <CardBody>
                    <Code
                      p={4}
                      borderRadius="md"
                      bg="gray.900"
                      color="white"
                      fontSize="sm"
                      overflowX="auto"
                      whiteSpace="pre"
                      w="100%"
                    >
                      {JSON.stringify(formData.layout_data, null, 2)}
                    </Code>
                  </CardBody>
                </Card>
              </TabPanel>
            </TabPanels>
          </Tabs>
        </VStack>
      </form>
    </Container>
  );
};

const LayoutSection = ({
  section,
  sectionIndex,
  components,
  onUpdateSection,
  onRemoveSection,
  onAddComponent,
  onRemoveComponent,
  cardBg,
  borderColor,
  sectionBg
}) => {
  const [isExpanded, setIsExpanded] = useState(true);

  return (
    <Card bg={cardBg} borderColor={borderColor} borderWidth="1px">
      <CardHeader>
        <HStack justify="space-between">
          <HStack>
            <IconButton
              icon={<FiMove />}
              size="sm"
              variant="ghost"
              cursor="grab"
            />
            <VStack align="start" spacing={0}>
              <Input
                value={section.name}
                onChange={(e) => onUpdateSection(sectionIndex, { name: e.target.value })}
                size="sm"
                variant="unstyled"
                fontWeight="semibold"
              />
              <Badge size="sm" colorScheme="blue">
                {section.type}
              </Badge>
            </VStack>
          </HStack>
          
          <HStack>
            <Select
              size="sm"
              placeholder="Add Component"
              onChange={(e) => {
                if (e.target.value) {
                  onAddComponent(sectionIndex, parseInt(e.target.value));
                  e.target.value = '';
                }
              }}
            >
              {components.map(component => (
                <option key={component.id} value={component.id}>
                  {component.name}
                </option>
              ))}
            </Select>
            
            <IconButton
              icon={<FiTrash2 />}
              size="sm"
              colorScheme="red"
              variant="ghost"
              onClick={() => onRemoveSection(sectionIndex)}
            />
          </HStack>
        </HStack>
      </CardHeader>

      <CardBody>
        <Accordion allowToggle index={isExpanded ? 0 : -1}>
          <AccordionItem border="none">
            <AccordionButton 
              onClick={() => setIsExpanded(!isExpanded)}
              px={0}
            >
              <Box flex="1" textAlign="left">
                <Text fontSize="sm" fontWeight="medium">
                  Section Settings & Components ({section.components.length})
                </Text>
              </Box>
              <AccordionIcon />
            </AccordionButton>
            <AccordionPanel px={0}>
              <VStack spacing={4} align="stretch">
                {/* Section Settings */}
                <Box bg={sectionBg} p={4} borderRadius="md">
                  <Text fontWeight="medium" mb={3}>Section Settings</Text>
                  <SimpleGrid columns={{ base: 1, md: 3 }} spacing={3}>
                    <FormControl>
                      <FormLabel fontSize="sm">Background</FormLabel>
                      <Input
                        size="sm"
                        value={section.settings.background}
                        onChange={(e) => onUpdateSection(sectionIndex, {
                          settings: { ...section.settings, background: e.target.value }
                        })}
                        placeholder="#ffffff"
                      />
                    </FormControl>
                    
                    <FormControl>
                      <FormLabel fontSize="sm">Padding</FormLabel>
                      <Input
                        size="sm"
                        value={section.settings.padding}
                        onChange={(e) => onUpdateSection(sectionIndex, {
                          settings: { ...section.settings, padding: e.target.value }
                        })}
                        placeholder="2rem"
                      />
                    </FormControl>
                    
                    <FormControl>
                      <FormLabel fontSize="sm">Max Width</FormLabel>
                      <Input
                        size="sm"
                        value={section.settings.maxWidth}
                        onChange={(e) => onUpdateSection(sectionIndex, {
                          settings: { ...section.settings, maxWidth: e.target.value }
                        })}
                        placeholder="1200px"
                      />
                    </FormControl>
                  </SimpleGrid>
                </Box>

                {/* Components List */}
                <Box>
                  <Text fontWeight="medium" mb={3}>Components</Text>
                  {section.components.length > 0 ? (
                    <VStack spacing={2} align="stretch">
                      {section.components.map((component, componentIndex) => (
                        <Flex
                          key={component.id}
                          p={3}
                          bg="white"
                          borderRadius="md"
                          border="1px solid"
                          borderColor="gray.200"
                          align="center"
                          justify="space-between"
                        >
                          <HStack>
                            <IconButton
                              icon={<FiMove />}
                              size="xs"
                              variant="ghost"
                              cursor="grab"
                            />
                            <VStack align="start" spacing={0}>
                              <Text fontSize="sm" fontWeight="medium">
                                {component.component_name}
                              </Text>
                              <Text fontSize="xs" color="gray.500">
                                {component.component_identifier}
                              </Text>
                            </VStack>
                          </HStack>
                          
                          <IconButton
                            icon={<FiTrash2 />}
                            size="xs"
                            colorScheme="red"
                            variant="ghost"
                            onClick={() => onRemoveComponent(sectionIndex, componentIndex)}
                          />
                        </Flex>
                      ))}
                    </VStack>
                  ) : (
                    <Box
                      p={6}
                      border="2px dashed"
                      borderColor="gray.300"
                      borderRadius="md"
                      textAlign="center"
                    >
                      <Text color="gray.500" fontSize="sm">
                        No components in this section
                      </Text>
                      <Text color="gray.400" fontSize="xs">
                        Use the dropdown above to add components
                      </Text>
                    </Box>
                  )}
                </Box>
              </VStack>
            </AccordionPanel>
          </AccordionItem>
        </Accordion>
      </CardBody>
    </Card>
  );
};

export default LayoutForm;